/**
 * The «Libreta de direcciones» (#159): what the party blocks' type-ahead, «Guardar en la libreta»
 * and the screen share. Since #221 it keeps places, vehicles, trailers and drivers beside the
 * parties, each `kind` with its own fields and its own tab.
 *
 * The book is the company's counterparties: every one its documents that stand carry, merged with
 * the entries it saved. The suggestions keep one per NIF; the list keeps every entry in reach, so the
 * office sees a Gestor's beside the company's. The server builds both on each read and decides whose
 * book a caller reaches and saves to (a Gestor Individual's own, everyone else's the company's), so
 * nothing here filters, merges or chooses a book.
 */

import type { components } from '@deca/api'

/**
 * One counterparty: a saved entry (`ENTRY`) or what the documents say (`HISTORY`). Aliased from the
 * generated schema, never re-declared: CI fails on drift between the client and the contract, so a
 * hand-written twin would be the one thing that could disagree with the API and still compile.
 */
export type AddressBookItem = components['schemas']['AddressBookItem']

/** A saved entry, as a write answers it. */
export type AddressBookEntry = components['schemas']['AddressBookEntry']

/** The body of a save or a correction. */
export type AddressBookEntryInput = components['schemas']['AddressBookEntryInput']

/** What an entry is: a party, a place, a vehicle, a trailer or a driver (#221). */
export type AddressBookKind = components['schemas']['AddressBookKind']

/**
 * The *Libreta*'s tabs, in their order, each as its `tipo` in the address. The URL speaks Spanish,
 * as the portal's other filters do; the API speaks the kinds.
 */
export const BOOK_TABS = [
  { tipo: 'empresas', kind: 'PARTY' },
  { tipo: 'lugares', kind: 'PLACE' },
  { tipo: 'vehiculos', kind: 'VEHICLE' },
  { tipo: 'remolques', kind: 'TRAILER' },
  { tipo: 'conductores', kind: 'DRIVER' },
] as const satisfies ReadonlyArray<{ tipo: string; kind: AddressBookKind }>

export type BookTipo = (typeof BOOK_TABS)[number]['tipo']

/** The kind a tab lists; a party for none. */
export const kindOfTipo = (tipo: BookTipo | undefined): AddressBookKind =>
  BOOK_TABS.find((tab) => tab.tipo === tipo)?.kind ?? 'PARTY'

/** What a row is called in a toast or a question: a name, else a place's address, else a plate. */
export const labelOf = (row: { name: string | null; address: string | null; plate: string | null }): string =>
  row.name ?? row.address ?? row.plate ?? ''

/**
 * The shortest search that is worth asking, once trimmed. The server's own `MIN_QUERY_LENGTH`
 * (`backend/src/addressbook/search.ts`), which answers anything shorter with an empty list: the
 * portal does not ask at all.
 */
export const SUGGESTION_MIN_LENGTH = 2

/** The most suggestions one search answers with — the server's `SUGGESTION_LIMIT`. */
export const SUGGESTION_LIMIT = 10

/**
 * Typed values as the contract wants them, for one kind: trimmed, with what is blank `null`, and only
 * the fields that kind takes, so nothing reaches the server that it would refuse as another kind's.
 * An address that is only spaces is no address, and storing it would print nothing on the next
 * document while claiming to hold one.
 *
 * A party's DeCA recipients and «Enviar automáticamente» (#219) only when given: a caller that saves
 * the party alone («Actualizar» from a create form) sends neither, and the server keeps the entry's own.
 */
export function entryInputOf(
  kind: AddressBookKind,
  values: {
    name?: string | null
    nif?: string | null
    address?: string | null
    plate?: string | null
    decaRecipients?: string[]
    decaAutoSend?: boolean
  },
): AddressBookEntryInput {
  const clean = (value: string | null | undefined) => {
    const trimmed = value?.trim() ?? ''
    return trimmed === '' ? null : trimmed
  }
  switch (kind) {
    case 'PARTY':
      return {
        kind,
        name: values.name?.trim() ?? '',
        nif: values.nif?.trim() ?? '',
        address: clean(values.address),
        ...(values.decaRecipients !== undefined ? { decaRecipients: values.decaRecipients } : {}),
        ...(values.decaAutoSend !== undefined ? { decaAutoSend: values.decaAutoSend } : {}),
      }
    case 'PLACE':
      return { kind, name: clean(values.name), address: clean(values.address) }
    case 'VEHICLE':
    case 'TRAILER':
      return { kind, plate: clean(values.plate) }
    case 'DRIVER':
      return { kind, name: values.name?.trim() ?? '', nif: values.nif?.trim() ?? '' }
  }
}
